import LoadingComponent from "../../../components/LoadingComponent.jsx";
import {Fragment, useEffect, useMemo, useState} from "react";
import {ProjectApi} from "../../../api/authority/generated/index";
import useAuthorityConfiguration from "../../../api/utilities/useAuthorityConfiguration.js";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import {useTranslation} from "react-i18next";
import Select from "react-select";
import Toggle from "../../../components/Toggle.jsx";
import Button from "../../../components/Button.jsx";

export default function RolePermissions(
    {
        project,
        roleId,
        onDelete = () => {},
    }
) {
    let {et} = useErrorTranslation();
    let {t} = useTranslation();

    const [refresh, setRefresh] = useState(false);
    const [role, setRole] = useState(null);
    const [channels, setChannels] = useState({});

    let authorityConfiguration = useAuthorityConfiguration();

    let permissions = useMemo(() => {
        let subscription = project.subscription;

        return [
            {
                key: 'allowsDailyOverview',
                title: t('route.daily-metrics'),
                description: t('route.daily-metrics-permission-description'),
            },
            {
                key: 'allowsMainOverview',
                title: t('route.performance-summary'),
                description: t('route.performance-summary-permission-description'),
            },
            {
                key: 'allowsDiscountsAndCoupons',
                title: t('route.sales-promotions'),
                description: t('route.sales-promotions-permission-description'),
                show: subscription?.currentPackage?.allowDiscountCodeEvaluation ?? false,
            },
            {
                key: 'allowsMarketingChannelOverview',
                title: t('route.campaign-performance'),
                description: t('route.campaign-performance-permission-description'),
            },
            {
                key: 'allowsProfitability',
                title: t('route.campaign-profitability'),
                description: t('route.campaign-profitability-description'),
            },
            {
                key: 'allowsProjectProfile',
                title: t('route.project-settings'),
                description: t('project.project-settings-description'),
            },
            {
                key: 'allowsProjectIntegrations',
                title: t('project.3rd-party-integrations'),
                description: t('project.3rd-party-integrations-permission-description'),
            },
            {
                key: 'allowsProjectBilling',
                title: t('route.project-billing'),
                description: t('route.project-billing-permission-description'),
            },
            {
                key: 'allowsProjectAffiliate',
                title: t('route.project-affiliate'),
                description: t('route.project-affiliate-permission-description'),
                show: subscription?.currentPackage?.allowAffiliateEvaluation ?? false,
            },
            {
                key: 'allowsProjectMailing',
                title: t('route.project-mailing'),
                description: t('route.project-mailing-permission-description'),
            },
            {
                key: 'allowsProjectUsers',
                title: t('route.project-users'),
                description: t('route.project-users-permission-description'),
            },
            {
                key: 'allowsProjectInvitations',
                title: t('route.project-invitations'),
                description: t('route.project-invitations-permission-description'),
            },
        ];
    }, [project.id]);

    useEffect(() => {

        if (refresh) {
            setRefresh(false);
            return;
        }

        if (roleId === null) {
            return;
        }

        setRole(null);

        new ProjectApi(authorityConfiguration)
            .getProjectRole({
                projectId: project.id,
                roleId: roleId,
            })
            .then((projectRoleOutput) => {
                setRole(projectRoleOutput.projectRole);
                setChannels(projectRoleOutput.channels);
            })
            .catch((e) => et(e).then(toast.error));

    }, [project.id, roleId, refresh]);

    let channelOptions = useMemo(() => {
        let options = [];

        for (let channelKey of Object.keys(channels)) {
            let channelValue = channels[channelKey];

            options.push({
                value: channelKey,
                label: channelValue,
            });
        }

        return options;
    }, [role, project.id, roleId]);

    let channelsDisabled = useMemo(() => {
        let options = [];

        for (let channelKey of Object.keys(channels)) {
            let channelValue = channels[channelKey];

            if (role?.forbiddenChannels?.includes(channelKey)) {
                options.push({
                    value: channelKey,
                    label: channelValue,
                });
            }

        }

        return options;
    }, [role, project.id, roleId]);

    const [updateRole, setUpdateRole] = useState(false);

    useEffect(() => {
        if (updateRole) {
            new ProjectApi(authorityConfiguration)
                .patchProjectRole({
                    projectId: project.id,
                    roleId: roleId,
                    patchRoleInput: role,
                })
                .then(() => {
                    setRefresh(true);
                })
                .catch((e) => et(e).then(toast.error));
        }

        setUpdateRole(false);
    }, [updateRole]);

    return <LoadingComponent loading={updateRole || (roleId !== null && role === null)}>
        {roleId === null && <div className={'pt-6 pb-6 text-sm text-center'}>{t('project.select-role-to-edit')}</div>}
        {roleId !== null && role !== null && <div>
            <div className={'p-6'}>
                <div className={'border-b border-gray-200 dark:border-gray-700/60'}>
                    <h2 className="text-2xl text-gray-800 dark:text-gray-100 font-bold mb-5">
                        {t('project.edit-x-role', {roleName: role.name})}
                    </h2>
                </div>

                <ul>
                    {Object.values(permissions).map((permission) => {

                    if (!(permission.show ?? true)) {
                        return <Fragment key={permission.key}></Fragment>;
                    }

                    return <li
                            className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700/60"
                            key={permission.key}
                        >
                            <div>
                                <div className="text-gray-800 dark:text-gray-100 font-semibold">
                                    {permission.title}
                                </div>
                                <div className="text-sm">
                                    {permission.description}
                                </div>
                            </div>

                            <div className="flex items-center ml-4">
                            <Toggle enabled={role[permission.key]} setEnabled={(enabled) => {
                                setRole((role) => {
                                    role[permission.key] = enabled;
                                    return role;
                                });
                                setUpdateRole(true);
                            }}/>
                            </div>
                        </li>
                            ;
                    })}
                </ul>

                <div className="mt-3 mb-2 text-gray-800 dark:text-gray-100 font-semibold">
                    {t('project.role-forbidden-channels')}
                </div>

                <Select
                    className={'custom-select'}
                    options={channelOptions}
                    defaultValue={channelsDisabled}
                    onChange={(e) => {

                        setRole((role) => {
                            // noinspection JSUnresolvedReference
                            role['forbiddenChannels'] = e.map((selectedChannel) => {
                                return selectedChannel.value;
                            });

                            return role;
                        });
                        setUpdateRole(true);


                    }}
                    isMulti
                />
                </div>

            <footer>
                <div className="flex flex-col px-6 py-3 border-t border-gray-200 dark:border-gray-700/60">
                    <div className="flex self-end">
                        <Button
                            variant={'outline-red'}
                            onClick={() => {
                                onDelete(roleId)
                            }}
                        >
                            {t('project.role-delete')}
                        </Button>
                    </div>
                </div>
            </footer>
        </div>
        }
    </LoadingComponent>
        ;
}
